import {
  useDeleteUiConfigMutation,
  useUnpublishUiConfigMutation,
  useUpdateUiConfigMutation,
} from 'api/uiConfig/uiConfig.api'
import { getMissingCalcVariables, mapReturnIdToVariables } from 'helpers/dataset.helper/dataset.calcDependencies.helper'
import { applyPreviewEntry } from 'helpers/uiConfig.helper/uiConfig.helper'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { useSnapshot } from 'valtio'

export type ActiveDialog =
  | { kind: 'delete' }
  | { kind: 'warn-missing-vars'; publishingUiConfig?: boolean; message: string }
  | { kind: 'review-draft'; publishingUiConfig?: boolean; warningMessage?: string }

export function useSaveControls() {
  const { mutate: updateUiConfig } = useUpdateUiConfigMutation()
  const { mutate: unpublishUiConfig } = useUnpublishUiConfigMutation()
  const { mutate: deleteUiConfigs } = useDeleteUiConfigMutation()
  const [activeDialog, setActiveDialog] = useState<ActiveDialog | undefined>()
  const navigate = useNavigate()
  const snap = useSnapshot(uiConfigStore)
  const { t } = useTranslation()

  const uid = snap.editingUid
  // Narrow at the snapshot boundary; downstream code works with the typed UiConfig.
  const rawUiConfig = uid != null ? (snap.uiConfigs[uid] as UiConfig) : undefined
  const hasDraft = uid != null ? snap.hasPreviewDraft(uid) : false
  const storeDraft = uid != null ? snap.previewCollection[uid] : undefined
  const isActionable = uid != null && rawUiConfig != null && (typeof rawUiConfig.id !== 'number' || rawUiConfig.id > 0)

  // The editor syncs valid JSON to the store draft on every keystroke, so storeDraft
  // is always at least as current as the editor's in-memory state when a user gesture
  // (Save, Ctrl+S) can reach this function.
  function resolveEffectiveConfig(config: UiConfig): UiConfig {
    return storeDraft != null ? applyPreviewEntry(config, storeDraft) : config
  }

  function save(config: UiConfig, publishingUiConfig?: boolean): void {
    if (!publishingUiConfig && !hasDraft) return

    const uiConfig: UiConfig = {
      ...resolveEffectiveConfig(config),
      is_published: publishingUiConfig === true,
    }
    if (publishingUiConfig) {
      uiConfig.mode = 'dev'
    }

    const savedUid = uiConfig.uid
    updateUiConfig(uiConfig, {
      onSuccess: () => {
        if (savedUid != null) {
          uiConfigStore.clearPreviewDraft(savedUid)
        }
      },
    })
  }

  function checkCalcsAndSave(publishingUiConfig?: boolean): void {
    if (uid == null || !rawUiConfig) return
    if (!publishingUiConfig && !hasDraft) return

    const effectiveConfig = resolveEffectiveConfig(rawUiConfig)
    const datasetInstructions = (effectiveConfig.dataset_instructions ?? []) as DatasetInstruction[]
    const missingVariables = getMissingCalcVariables(datasetInstructions, mapReturnIdToVariables(datasetInstructions))

    if (missingVariables.size > 0) {
      const message = `${t('The UiConfig has calcs with missing variables')}: ${Array.from(missingVariables)
        .map((v) => `"${v}"`)
        .join(', ')}`

      if (hasDraft) {
        setActiveDialog({ kind: 'review-draft', publishingUiConfig, warningMessage: message })
        return
      }

      setActiveDialog({ kind: 'warn-missing-vars', publishingUiConfig, message })
      return
    }

    if (hasDraft) {
      setActiveDialog({ kind: 'review-draft', publishingUiConfig })
      return
    }

    save(rawUiConfig, publishingUiConfig)
  }

  // The diff viewer in the review-draft dialog should show what will actually be
  // saved, not the raw storeDraft patch. When storeDraft is a partial patch
  // (written via setPreviewDraftPatch and missing the display_name discriminator),
  // applyPreviewEntry merges it with rawUiConfig at save-time. Showing the raw
  // patch would make the diff look like top-level fields (display_name, etc.)
  // will be removed even though they are preserved by the merge.
  const effectiveDraftConfig = rawUiConfig != null ? resolveEffectiveConfig(rawUiConfig) : undefined
  const effectiveDraftFields =
    effectiveDraftConfig != null
      ? {
          display_name: effectiveDraftConfig.display_name,
          props: effectiveDraftConfig.props,
          children_ids: effectiveDraftConfig.children_ids,
          dataset_instructions: effectiveDraftConfig.dataset_instructions,
          alias: effectiveDraftConfig.alias,
        }
      : undefined

  // Delete is only possible when the config has been persisted (has a numeric id).
  const canDelete = rawUiConfig != null && typeof rawUiConfig.id === 'number' && rawUiConfig.id > 0

  return {
    uid,
    rawUiConfig,
    storeDraft,
    hasDraft,
    isActionable,
    canDelete,
    effectiveDraftFields,
    activeDialog,
    handleSave: () => checkCalcsAndSave(false),
    handlePublish: () => checkCalcsAndSave(true),
    handleUnpublish: () => {
      if (uid != null) unpublishUiConfig(uid)
    },
    handleDeleteClick: () => setActiveDialog({ kind: 'delete' }),
    handleConfirmDelete: () => {
      if (!canDelete || uid == null || rawUiConfig?.id == null) {
        setActiveDialog(undefined)
        return
      }
      deleteUiConfigs(rawUiConfig.id, {
        onSuccess: () => navigate('/uiconfig'),
      })
      setActiveDialog(undefined)
    },
    handleConfirmSave: () => {
      if (rawUiConfig && activeDialog && activeDialog.kind !== 'delete') {
        save(rawUiConfig, activeDialog.publishingUiConfig)
      }
      setActiveDialog(undefined)
    },
    handleDismissDialog: () => setActiveDialog(undefined),
  }
}
